import cx from 'classnames'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { ReactCountryFlag } from 'react-country-flag'
import { CSVLink } from 'react-csv'

import { COUNTRIES, NO_DATA_MESSAGE } from '../../../utils/constants'
import {
  formatDollars,
  formatPercentOneDecimal,
  formatThousand,
} from '../../../utils/functions'
import AsOfDate from '../../AsOfDate'
import ScrollingTable from '../../Tables/ScrollingTable'
import { getFileName, getTotalMarketValue, getTotalNetAssets } from './index'

const flagStyles = { width: '25px', height: '25px' }

class FullHoldingsEquity extends Component {
  constructor(props) {
    super(props)
    this.renderThousands = this.renderThousands.bind(this)

    const data = get(props, 'data.fullHoldings', []) || []
    const asofdate = get(data, '[0].calendar.date', '')
    const fileName = getFileName(props.data?.name, asofdate)

    this.columns = [
      {
        Header: 'Company Name [Ticker]',
        id: 'securityName',
        accessor: (holding) =>
          holding.securityName +
          (holding.ticker && holding.ticker.trim()
            ? ` [${holding.ticker}]`
            : ''),
      },
      {
        Header: 'Country',
        id: 'countryName',
        accessor: (holding) => {
          if (holding.countryName) {
            const countryCode =
              COUNTRIES.find(
                ({ Name }) =>
                  Name.toLowerCase() === holding.countryName.toLowerCase()
              )?.Code ?? ''
            return countryCode ? (
              <ReactCountryFlag
                countryCode={countryCode}
                svg
                style={flagStyles}
                aria-label={`${holding.countryName} Flag`}
                title={`${holding.countryName} Flag`}
                alt={`${holding.countryName} Flag`}
              />
            ) : null
          }
          return ''
        },
      },
      {
        Header: "Shares (000's)",
        id: 'shares',
        accessor: 'shares',
        Cell: this.renderThousands,
      },
      {
        Header: 'Current Price ($)',
        id: 'price',
        accessor: 'price',
        Cell: ({ value }) => {
          if (!value || isNaN(Number(value))) {
            return <span>--</span>
          }
          return <span>{formatDollars(value)}</span>
        },
      },
      {
        Header: "Market Value ($000's)",
        id: 'marketValue',
        accessor: 'marketValue',
        Cell: this.renderThousands,
      },
      {
        Header: '% of Net Assets',
        id: 'weight',
        accessor: 'weight',
        Cell: ({ value }) => <span>{formatPercentOneDecimal(value)}</span>,
      },
    ]

    this.fileHeaders = [
      { label: 'TICKER', key: 'ticker' },
      { label: 'NAME', key: 'securityName' },
      { label: 'SHARES/PRINCIPAL', key: 'shares' },
      { label: 'PRICE (USD)', key: 'price' },
      { label: 'VALUE (USD)', key: 'marketValue' },
      { label: '% OF TOTAL NET ASSETS', key: 'weight' },
    ]

    this.filteredData = data.filter((obj) => obj.ticker)

    const totalMarketValue = getTotalMarketValue(data)
    const totalNetAssetsWeight = getTotalNetAssets(data)
    const finalNetAssetsWeight = totalNetAssetsWeight / 100

    this.footerData = [
      {
        securityName: 'Total',
        countryName: '',
        shares: '',
        price: '',
        marketValue: totalMarketValue,
        weight: finalNetAssetsWeight,
      },
      {
        securityName: 'Cash and Other Assets Less Liabilities',
        countryName: '',
        shares: '',
        price: '',
        marketValue: '',
        weight: 1 - finalNetAssetsWeight,
      },
      {
        securityName: 'Total Net Assets',
        countryName: '',
        shares: '',
        price: '',
        marketValue: '',
        weight: 1,
      },
    ]

    this.scrollingTableOptions = {
      presentation: props.presentation,
      stripedTable: true,
    }

    this.state = {
      asofdate,
      fullHoldings: data,
      fileName,
      isClient: false,
    }
  }

  componentDidMount() {
    this.setState({ isClient: true })
  }

  renderThousands({ value }) {
    if (!value || isNaN(Number(value))) {
      return <span>--</span>
    }
    return <span>{formatThousand(value, 0)}</span>
  }

  render() {
    const { fiscalQuarterEndDoc } = this.props
    const { asofdate, fullHoldings, fileName, isClient } = this.state

    if (!fullHoldings.length) {
      return <p>{NO_DATA_MESSAGE}</p>
    }

    return (
      <>
        <AsOfDate date={asofdate} />
        <div className={cx('doc-links', 'withHarborThemeWrapper')}>
          {'Download: '}
          <span suppressHydrationWarning>
            {isClient ? (
              <CSVLink
                filename={fileName}
                headers={this.fileHeaders}
                data={fullHoldings}
              >
                Full Holdings (csv)
              </CSVLink>
            ) : (
              'Full Holdings (csv)'
            )}
          </span>
          {fiscalQuarterEndDoc && ' | '}
          {fiscalQuarterEndDoc?.localAsset?.publicURL && (
            <a
              href={fiscalQuarterEndDoc.localAsset.publicURL}
              target="_blank"
              rel="noreferrer"
            >
              Fiscal Quarter-End Holdings (pdf)
            </a>
          )}
        </div>

        <ScrollingTable
          columns={this.columns}
          data={this.filteredData}
          footerData={this.footerData}
          options={this.scrollingTableOptions}
        />
      </>
    )
  }
}

FullHoldingsEquity.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    fullHoldings: PropTypes.arrayOf(
      PropTypes.shape({
        ticker: PropTypes.string,
        securityName: PropTypes.string,
        shares: PropTypes.number,
        price: PropTypes.number,
        marketValue: PropTypes.number,
        weight: PropTypes.number,
        countryName: PropTypes.string,
        calendar: PropTypes.shape({
          date: PropTypes.string,
        }),
      })
    ),
  }),
  presentation: PropTypes.object,
  fiscalQuarterEndDoc: PropTypes.shape({
    localAsset: PropTypes.shape({
      publicURL: PropTypes.string,
    }),
  }),
}

export default FullHoldingsEquity